import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './builder-root.css';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../components/ui/resizable';
import {
    Rocket, MessageCircle, History, Paintbrush, Upload, Save,
    Code2, Eye, Sparkles, Clock, CheckCircle2, AlertCircle, Loader2, Settings,
    Paperclip, Bot, User, Cpu, Send, Zap, FileText, X, Globe, Layers,
    LayoutGrid, ChevronDown, Shield, Wifi, Terminal, FileBarChart
} from 'lucide-react';
import { toast } from 'sonner';
import api, { BACKEND_URL } from '../utils/api';
import LivePreview from '../components/LivePreview';
import StaticPreview from '../components/StaticPreview';
import VisualEditor from '../components/VisualEditor';
import SectionEditor from '../components/SectionEditor';
import DeploymentConsole from '../components/DeploymentConsole';
import AgentConsole from '../components/AgentConsole';
import BuildReport from '../components/BuildReport';
import FileUploadZone from '../components/FileUploadZone';
import ChatPanel from '../components/ChatPanel';
import VoiceInputButton from '../components/VoiceInputButton';
import NinjaGuide from '../components/NinjaGuide';

const normalizePathForPreview = (path) => (path || '').replace(/\\/g, '/');

const ensureUniquePath = (path, registry) => {
    if (!path) {
        return path;
    }
    let candidate = path;
    const extensionMatch = candidate.match(/\.[^.]+$/);
    const extension = extensionMatch ? extensionMatch[0] : '';
    const base = extensionMatch ? candidate.slice(0, -extension.length) : candidate;
    let counter = 2;
    while (registry.has(candidate)) {
        candidate = `${base}_${counter}${extension}`;
        counter += 1;
    }
    registry.add(candidate);
    return candidate;
};

const extractDefaultExportName = (content) => {
    if (typeof content !== 'string' || !content.trim()) {
        return null;
    }
    const defaultFn = content.match(/export\s+default\s+function\s+([A-Za-z0-9_]+)/);
    if (defaultFn && defaultFn[1] && defaultFn[1] !== 'function') {
        return defaultFn[1];
    }
    const defaultClass = content.match(/export\s+default\s+class\s+([A-Za-z0-9_]+)/);
    if (defaultClass && defaultClass[1] && defaultClass[1] !== 'class') {
        return defaultClass[1];
    }
    const assignedDefault = content.match(/export\s+default\s+([A-Za-z0-9_]+)\s*;/);
    if (assignedDefault && assignedDefault[1]) {
        return assignedDefault[1];
    }
    return null;
};

const alignReactFilesForPreview = (files = []) => {
    const normalized = files.map((file) => ({
        ...file,
        path: normalizePathForPreview(file?.path || ''),
    }));

    const appFile = normalized.find((file) => /src\/App\.(js|jsx|ts|tsx)$/i.test(file?.path || ''));
    const importMap = {};

    if (appFile && typeof appFile.content === 'string') {
        const importRegex = /import\s+([A-Za-z0-9_]+)\s+from\s+['"](\.\/?[^'";]+)['"]/g;
        let match = importRegex.exec(appFile.content);
        while (match) {
            const [, identifier, relPath] = match;
            if (identifier && relPath && relPath.startsWith('./')) {
                importMap[identifier] = relPath;
            }
            match = importRegex.exec(appFile.content);
        }
    }

    const usedPaths = new Set();

    return normalized.map((file) => {
        if (!file || typeof file.path !== 'string') {
            return file;
        }

        const currentPath = file.path;
        const isSrcFile = currentPath.toLowerCase().startsWith('src/');
        let desiredPath = currentPath;

        if (isSrcFile) {
            const exportName = extractDefaultExportName(file.content);

            if (exportName && exportName !== 'App' && importMap[exportName]) {
                const extensionMatch = currentPath.match(/\.[^.]+$/);
                const extension = extensionMatch ? extensionMatch[0] : '.js';
                let relativeTarget = importMap[exportName]
                    .replace(/^\.\//, '')
                    .replace(/\.\.\//g, '');
                if (!relativeTarget.startsWith('src/')) {
                    relativeTarget = `src/${relativeTarget}`;
                }
                if (!/\.[^.]+$/.test(relativeTarget)) {
                    relativeTarget = `${relativeTarget}${extension}`;
                }
                relativeTarget = relativeTarget.replace(/\/+/g, '/');
                desiredPath = relativeTarget;
            }
        }

        const finalPath = ensureUniquePath(desiredPath, usedPaths);
        if (finalPath !== currentPath) {
            return { ...file, path: finalPath };
        }
        return file;
    });
};

const prepareProjectForPreview = (project) => {
    if (!project || typeof project !== 'object') {
        return project;
    }
    const alignedFiles = alignReactFilesForPreview(project.files || []);
    return {
        ...project,
        files: alignedFiles,
    };
};

/**
 * Enhanced Builder - All Replit/Emergent-level features
 * - Full-Stack Builder (200+ min builds)
 * - Visual Editor (click-to-edit)
 * - Version Control (snapshots)
 * - Discussion Mode (plan without building)
 * - One-Click Deployment
 * - Auto Database Setup
 */
const EnhancedBuilder = () => {
    const navigate = useNavigate();
    const { projectId } = useParams();

    const [mode, setMode] = useState('build'); // build, discuss, edit, deploy
    const [buildMode, setBuildMode] = useState(() => {
        // Load from localStorage or default to 'full_ai' (LLM-powered)
        const saved = localStorage.getItem('build_mode');
        return saved === 'FORTRESS' ? 'static' : 'full_ai';
    });
    const [aiImages, setAiImages] = useState(false); // DALL-E image generation when true
    const [preset, setPreset] = useState(''); // '' = generic
    const [aiModel, setAiModel] = useState('gpt-4o'); // AI model selector
    const [prompt, setPrompt] = useState('');
    const [building, setBuilding] = useState(false);
    const [artifacts, setArtifacts] = useState(null); // Phase 2: artifact contract data
    const [progress, setProgress] = useState([]);
    const [project, setProject] = useState(null);
    const [snapshots, setSnapshots] = useState([]);
    const [discussionHistory, setDiscussionHistory] = useState(() => {
        // Get personalized greeting based on user and time of day
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userName = user.name ? user.name.split(' ')[0] : null; // First name only
        const hour = new Date().getHours();

        let timeGreeting = 'Hello';
        if (hour >= 5 && hour < 12) timeGreeting = 'Good morning';
        else if (hour >= 12 && hour < 17) timeGreeting = 'Good afternoon';
        else if (hour >= 17 && hour < 21) timeGreeting = 'Good evening';
        else timeGreeting = 'Hello';

        const personalGreeting = userName ? `${timeGreeting}, ${userName}!` : `${timeGreeting}!`;

        return [{
            role: 'assistant',
            content: `🥷 **${personalGreeting}** I'm your Digital Ninja - your AI partner for building amazing websites.

**Let's create something together!** I'll guide you through the entire process:

1. **Tell me your vision** - Describe what you want to build
2. **I'll research & plan** - You'll see my thinking in real-time
3. **Watch it come to life** - Preview your site as I build it
4. **We'll refine together** - Ask me to tweak anything

**Ready?** Try: *"Build a luxury spa website with gold and black theme"* or just tell me about your business!`
        }];
    });
    const [activeTab, setActiveTab] = useState('preview');
    const [showDeploymentConsole, setShowDeploymentConsole] = useState(false);
    const [planner, setPlanner] = useState(null);
    const [research, setResearch] = useState(null);
    const [isMobileLayout, setIsMobileLayout] = useState(false);
    const [buildSummary, setBuildSummary] = useState(null);
    const [discussionLoading, setDiscussionLoading] = useState(false);
    const [projectLoading, setProjectLoading] = useState(false);
    const [backgroundMotion, setBackgroundMotion] = useState(() => {
        // Load preference from localStorage or default to true
        const saved = localStorage.getItem('dn-background-motion');
        return saved !== null ? saved === 'true' : true;
    });
    const [showSettings, setShowSettings] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [showUploadZone, setShowUploadZone] = useState(false);
    const [heroImage, setHeroImage] = useState(null); // {mime, b64}
    const [previewKey, setPreviewKey] = useState(0);

    // Agent Console + Build Report state
    const [consoleTab, setConsoleTab] = useState('input'); // 'input' | 'console' | 'report'
    const [visualProfile, setVisualProfile] = useState(null);

    // ── Task 8: Layout Pack state ──
    const [layoutPack, setLayoutPack] = useState('auto');
    const [layoutOverrides, setLayoutOverrides] = useState({});
    const [layoutPlanInfo, setLayoutPlanInfo] = useState(null);
    const [showAdvancedLayout, setShowAdvancedLayout] = useState(false);

    // Persist build mode preference
    useEffect(() => {
        localStorage.setItem('build_mode', buildMode === 'full_ai' ? 'FULL_AI' : 'FORTRESS');
    }, [buildMode]);

    // Persist motion preference
    useEffect(() => {
        localStorage.setItem('dn-background-motion', String(backgroundMotion));
    }, [backgroundMotion]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }
        const updateBreakpoint = () => {
            setIsMobileLayout(window.innerWidth < 1024);
        };
        updateBreakpoint();
        window.addEventListener('resize', updateBreakpoint);
        return () => {
            window.removeEventListener('resize', updateBreakpoint);
        };
    }, []);

    const applyProjectResponse = (projectPayload, fallbackPlanner = null, fallbackResearch = null) => {
        if (!projectPayload) {
            return;
        }
        const prepared = prepareProjectForPreview(projectPayload);
        setProject(prepared);
        setPlanner(prepared.planner || fallbackPlanner || null);
        setResearch(
            prepared.research ||
            prepared.planner?.research ||
            fallbackResearch ||
            null
        );
    };
    const normalizeResearchSummary = (summary) => {
        if (!summary) return [];
        if (Array.isArray(summary)) {
            return summary.filter(Boolean);
        }
        if (typeof summary === 'string') {
            return summary
                .split(/\r?\n+/)
                .map((line) => line.trim())
                .filter(Boolean);
        }
        return [];
    };

    const taskStatusConfig = {
        pending: {
            label: 'Pending',
            icon: Clock,
            iconClass: 'text-slate-500',
            borderClass: 'border-slate-700'
        },
        in_progress: {
            label: 'In Progress',
            icon: Loader2,
            iconClass: 'text-blue-400 animate-spin',
            borderClass: 'border-blue-500/40'
        },
        completed: {
            label: 'Completed',
            icon: CheckCircle2,
            iconClass: 'text-green-400',
            borderClass: 'border-green-500/40'
        },
        complete: {
            label: 'Completed',
            icon: CheckCircle2,
            iconClass: 'text-green-400',
            borderClass: 'border-green-500/40'
        },
        blocked: {
            label: 'Blocked',
            icon: AlertCircle,
            iconClass: 'text-yellow-400',
            borderClass: 'border-yellow-500/40'
        },
        failed: {
            label: 'Failed',
            icon: AlertCircle,
            iconClass: 'text-red-400',
            borderClass: 'border-red-500/40'
        }
    };

    const progressIconConfig = {
        success: {
            icon: CheckCircle2,
            iconClass: 'text-green-500'
        },
        error: {
            icon: AlertCircle,
            iconClass: 'text-red-500'
        },
        warning: {
            icon: AlertCircle,
            iconClass: 'text-yellow-400'
        },
        info: {
            icon: Clock,
            iconClass: 'text-blue-500'
        },
        phase: {
            icon: Sparkles,
            iconClass: 'text-orange-400'
        },
        plan_update: {
            icon: Sparkles,
            iconClass: 'text-purple-400'
        },
        plan: {
            icon: Sparkles,
            iconClass: 'text-orange-400'
        },
        research: {
            icon: Sparkles,
            iconClass: 'text-emerald-400'
        }
    };

    const formatTime = (value) => {
        if (!value) return null;
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return null;
        return date.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Africa/Johannesburg' });
    };

    const formatCategory = (category) => {
        if (!category) return '';
        return category.charAt(0).toUpperCase() + category.slice(1);
    };

    const plannerStats = useMemo(() => {
        if (!planner || !Array.isArray(planner.tasks) || planner.tasks.length === 0) {
            return null;
        }
        const completed = planner.tasks.filter(task => task.status === 'completed').length;
        const failed = planner.tasks.filter(task => task.status === 'failed').length;
        const activeTask = planner.tasks.find(task => task.status === 'in_progress') || null;
        const nextTask = planner.tasks.find(task => task.status === 'pending') || null;
        const remaining = planner.tasks.length - completed - failed;
        return {
            total: planner.tasks.length,
            completed,
            failed,
            remaining: remaining < 0 ? 0 : remaining,
            activeTask,
            nextTask
        };
    }, [planner]);

    const activeTaskId = plannerStats?.activeTask?.id;
    const nextTaskId = plannerStats?.nextTask?.id;
    const researchUpdated = research?.generated_at ? formatTime(research.generated_at) : null;

    useEffect(() => {
        if (projectId) {
            loadProject();
            loadSnapshots();
        }
    }, [projectId]);

    const generateBuildSummary = (files, projectName) => {
        if (!Array.isArray(files) || files.length === 0) return null;

        const pageCount = files.filter(f =>
            f.path.includes('pages/') || f.path.includes('Pages/')
        ).length;
        const componentCount = files.filter(f =>
            f.path.includes('components/') || f.path.includes('Components/')
        ).length;
        const hasCSS = files.some(f => f.path.includes('.css'));
        const hasTests = files.some(f => f.path.includes('.test.') || f.path.includes('.spec.'));

        return {
            projectName: projectName || 'Your App',
            totalFiles: files.length,
            pageCount,
            componentCount,
            hasCSS,
            hasTests,
            fileList: files.slice(0, 10).map(f => f.path),
            moreFiles: files.length > 10 ? files.length - 10 : 0,
            timestamp: new Date().toLocaleString('en-ZA', { hour12: false, timeZone: 'Africa/Johannesburg' })
        };
    };

    // ── Generate post-build AI summary message ──
    const generateAISummaryMessage = (summary, userPrompt) => {
        if (!summary) return null;

        // Get user's name for personalization
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const userName = user.name ? user.name.split(' ')[0] : '';

        // Smart suggestions based on what was built
        const suggestions = [];
        if (summary.pageCount <= 1) suggestions.push('🏠 Add more pages like "About Us" or "Contact"');
        if (!summary.hasCSS) suggestions.push('🎨 Add custom styling with your brand colors');
        if (summary.componentCount < 3) suggestions.push('🧩 Add a testimonials or features section');
        suggestions.push('📸 "Add a photo gallery to showcase my work"');
        suggestions.push('💬 "Add a contact form with email integration"');
        suggestions.push('🎯 "Make the hero section more eye-catching"');
        suggestions.push('📱 "Improve the mobile layout"');

        // Pick 3 random suggestions
        const pickedSuggestions = suggestions.sort(() => Math.random() - 0.5).slice(0, 3);

        const greeting = userName ? `Great work, ${userName}! 🎉` : 'Great work! 🎉';

        return `🥷 **${greeting} Your site is ready!**

Here's what I built for you:
• **${summary.totalFiles}** files created
• **${summary.pageCount || 'Multiple'}** pages designed
• **${summary.componentCount || 'Several'}** components integrated

---

**💡 Ideas to make it even better:**
${pickedSuggestions.map(s => `${s}`).join('\n')}

---

*Just tell me what you'd like to change - I'm here to help make your vision perfect!*`;
    };

    const loadProject = async () => {
        setProjectLoading(true);
        try {
            const response = await api.get(`/projects/${projectId}`);
            applyProjectResponse(response.data);
        } catch (error) {
            toast.error('Failed to load project');
        } finally {
            setProjectLoading(false);
        }
    };

    const loadSnapshots = async () => {
        try {
            const response = await api.get(`/projects/${projectId}/snapshots`);
            setSnapshots(response.data.snapshots || []);
        } catch (error) {
            console.error('Failed to load snapshots');
        }
    };

    // ── Hero Image Upload Handler ──
    const handleHeroImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowed = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
        if (!allowed.includes(file.type)) {
            toast.error('Only PNG, JPG, WebP, or GIF images allowed');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            toast.error(`Image too large (${Math.round(file.size / 1024)}KB). Max 5MB.`);
            return;
        }

        try {
            const formData = new FormData();
            formData.append('file', file);
            const token = localStorage.getItem('token') || '';
            const resp = await fetch(`${BACKEND_URL}/api/upload-hero-image`, {
                method: 'POST',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                body: formData,
            });
            if (!resp.ok) {
                const err = await resp.json().catch(() => ({}));
                throw new Error(err.detail || 'Upload failed');
            }
            const data = await resp.json();
            setHeroImage(data.hero_image);
            toast.success(`Hero image attached (${data.size_kb}KB)`);
        } catch (err) {
            toast.error(err.message || 'Failed to upload hero image');
        }
    };

    const startAutonomousBuild = async () => {
        if (!prompt.trim()) {
            toast.error('Please describe what you want to build');
            return;
        }

        // VIP Access Check - verify user has VIP plan or admin status before building
        try {
            const response = await api.get('/auth/me');
            const user = response.data;
            const hasVipAccess = user?.is_admin || (user?.is_active && user?.plan === 'vip');
            if (!hasVipAccess) {
                toast.error('VIP subscription required to build websites');
                navigate('/subscription');
                return;
            }
        } catch (err) {
            toast.error('Please log in to build');
            navigate('/login');
            return;
        }

        setBuilding(true);
        setProgress([]);
        setPlanner(null);
        setResearch(null);
        setBuildSummary(null);
        setArtifacts(null);
        setVisualProfile(null);
        setConsoleTab('console'); // auto-switch to Agent Console
        document.body.classList.add('agent-active');

        // Add "thinking" message to discussion
        setDiscussionHistory(prev => [...prev, {
            role: 'assistant',
            content: `🔨 **Building your site...**\n\nI'm working on: "${(prompt || 'your request').slice(0, 60)}${prompt?.length > 60 ? '...' : ''}"\n\nWatch the Agent Console for live progress!`
        }]);

        try {
            const token = localStorage.getItem('token') || '';
            const headers = {
                'Content-Type': 'application/json'
            };
            if (token) {
                headers.Authorization = `Bearer ${token}`;
            }

            // Build context from uploaded files
            const contextFiles = uploadedFiles.map(f => ({
                file_id: f.file_id,
                filename: f.filename,
                url: f.url,
                category: f.category
            }));

            // Enhance prompt with file context if files are attached
            let enhancedPrompt = prompt;
            if (contextFiles.length > 0) {
                const fileList = contextFiles.map(f => f.filename).join(', ');
                enhancedPrompt = `${prompt}\n\n[Context: User has attached ${contextFiles.length} file(s) for reference: ${fileList}]`;
            }

            // Phase 2: Both modes route through /static/stream
            // The FULL_AI branch within static/stream uses generate_full_ai_site()
            // which calls the LLM and falls back to Fortress if LLM fails.
            // static → fortress deterministic fallback (no LLM)
            const endpoint = `${BACKEND_URL}/api/projects/static/stream`;

            const techStack = { type: 'static', frontend: 'HTML/CSS/JS' };

            // Map UI buildMode to backend render_mode
            // full_ai → render_mode: 'ai' (LLM-powered generation)
            // static → render_mode: 'fortress' (deterministic fallback)
            const renderMode = buildMode === 'full_ai' ? 'ai' : 'fortress';
            const backendBuildMode = buildMode === 'full_ai' ? 'FULL_AI' : 'FORTRESS';

            // Extract project name from prompt for brand identity
            const extractedName = (() => {
                const m = enhancedPrompt.match(/(?:called|named|for)\s+['"]?([A-Z][A-Za-z0-9 &'.]+)/)
                    || enhancedPrompt.match(/['"]([^'"]{2,40})['"]/);
                return m ? m[1].trim().replace(/\.+$/, '') : enhancedPrompt.split(/[.!?,\n]/)[0].slice(0, 40).trim();
            })();

            const isFullAI = buildMode === 'full_ai';

            const response = await fetch(endpoint, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    prompt: enhancedPrompt,
                    project_name: extractedName || undefined,
                    tech_stack: techStack,
                    build_mode: backendBuildMode,
                    render_mode: renderMode,  // 'ai' for LLM, 'fortress' for deterministic
                    context_files: contextFiles,
                    ...(isFullAI ? { confirm_full_ai: true } : {}),
                    ...(isFullAI && aiImages ? { ai_images: true } : {}),
                    ...(isFullAI && aiModel ? { ai_model: aiModel } : {}),
                    ...(preset ? { preset } : {}),
                    ...(layoutPack && layoutPack !== 'auto' ? { layout_pack: layoutPack } : {}),
                    ...(Object.keys(layoutOverrides).length > 0 ? { layout_overrides: layoutOverrides } : {}),
                    ...(heroImage ? { hero_image: heroImage } : {}),
                })
            });

            if (!response.ok || !response.body) {
                let message = `Request failed with status ${response.status || 0}`;
                try {
                    const payload = await response.clone().json();
                    if (payload?.detail) {
                        message = Array.isArray(payload.detail)
                            ? payload.detail.map((item) => item.msg || item).join(', ')
                            : payload.detail;
                    }
                } catch (_) {
                    try {
                        const text = await response.clone().text();
                        if (text) {
                            message = text;
                        }
                    } catch (__) {
                        /* swallow */
                    }
                }
                throw new Error(message);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let shouldStop = false;
            let buffer = ''; // Buffer for incomplete lines

            while (!shouldStop) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                // Keep the last incomplete line in buffer
                buffer = lines.pop() || '';

                for (const rawLine of lines) {
                    const line = rawLine?.trim();
                    if (!line || !line.startsWith('data:')) {
                        continue;
                    }

                    const payload = line.slice(5).trim();
                    if (!payload) {
                        continue;
                    }

                    if (payload === '[DONE]') {
                        shouldStop = true;
                        break;
                    }

                    try {
                        const data = JSON.parse(payload);
                        const timestamp = data.timestamp || new Date().toISOString();

                        setProgress(prev => [...prev, {
                            type: data.type,
                            message: data.message,
                            timestamp
                        }]);

                        if (data.planner) {
                            setPlanner(data.planner);
                            if (data.planner.research) {
                                setResearch(data.planner.research);
                            }
                        }

                        if (data.task) {
                            setPlanner(prev => {
                                if (!prev || !Array.isArray(prev.tasks)) {
                                    return prev;
                                }
                                const tasks = prev.tasks.map(task =>
                                    task.id === data.task.id ? { ...task, ...data.task } : task
                                );
                                return { ...prev, tasks };
                            });
                        }

                        if (data.research) {
                            setResearch(data.research);
                            setPlanner(prev => {
                                if (!prev) return prev;
                                return { ...prev, research: data.research };
                            });
                        }

                        // Phase 2.4: Handle errors explicitly
                        if (data.type === 'error') {
                            const code = data.error_code || '';
                            const failedChecks = Array.isArray(data.failed_checks) ? data.failed_checks : [];
                            const diagnostics = data.diagnostics || null;
                            const message = data.message || data.error_message || 'Build failed';
                            const userMessage = code ? `[${code}] ${message}` : message;

                            console.error('Build error:', {
                                code,
                                message,
                                failedChecks,
                                diagnostics,
                            });
                            toast.error(userMessage);

                            if (failedChecks.length > 0) {
                                toast.error(`Checks failed: ${failedChecks.slice(0, 3).join(' | ')}`);
                            }
                            shouldStop = true;
                            break;
                        }

                        if (data.type === 'complete') {
                            // Artifacts are set ONLY from the API response below (single source of truth)
                            // SSE artifacts are intentionally ignored to prevent async race / flicker

                            // Task 8: Store resolved layout plan
                            if (data.layout_plan) {
                                setLayoutPlanInfo(data.layout_plan);
                            }

                            // Store visual profile from SSE complete event
                            if (data.visual_profile) {
                                setVisualProfile(data.visual_profile);
                            }

                            // Auto-switch to Build Report tab
                            setConsoleTab('report');

                            toast.success(buildMode === 'full_ai'
                                ? `Full AI build complete! ${data.page_count || 0} pages + manifest generated.`
                                : `Fortress build complete! ${data.page_count || 0} pages generated.`);

                            // Honest fallback banner: notify user if FULL_AI degraded to Fortress offline
                            if (buildMode === 'full_ai' && data.fallback_used) {
                                toast.warning(
                                    '\u26A0\uFE0F FULL_AI degraded: provider unavailable \u2014 delivered premium offline fallback',
                                    { duration: 8000 }
                                );
                            }

                            // Fetch project from API (files no longer sent via SSE to avoid payload corruption)
                            if (data.project_id) {
                                try {
                                    const projectResp = await api.get(`/projects/${data.project_id}`);
                                    if (projectResp.data && Array.isArray(projectResp.data.files) && projectResp.data.files.length > 0) {
                                        applyProjectResponse(projectResp.data, data.planner, data.research);
                                        setPreviewKey(Date.now());
                                        if (projectResp.data.artifacts) {
                                            setArtifacts(projectResp.data.artifacts);
                                        }

                                        const projectName = projectResp.data.name || (prompt || 'Build').slice(0, 40);
                                        const summary = generateBuildSummary(projectResp.data.files, projectName);
                                        setBuildSummary(summary);

                                        const aiMessage = generateAISummaryMessage(summary, prompt);
                                        if (aiMessage) {
                                            setDiscussionHistory(prev => [...prev, {
                                                role: 'assistant',
                                                content: aiMessage
                                            }]);
                                        }

                                        setActiveTab('preview');
                                        window.history.replaceState(null, '', `/builder/${data.project_id}`);
                                        // Create snapshot in background
                                        api.post(`/projects/${data.project_id}/snapshots`, {
                                            message: 'Initial build'
                                        }).catch(err => console.error('Snapshot failed:', err));
                                    } else {
                                        console.warn('Build complete but no files in project');
                                        toast.error('Build failed: No files were generated');
                                    }
                                } catch (fetchErr) {
                                    console.error('Failed to fetch project:', fetchErr);
                                    toast.error('Failed to load generated project. Please refresh and check My Projects.');
                                }
                            } else {
                                console.warn('Build complete but no project_id received');
                                toast.error('Build failed: No project ID returned');
                            }

                            shouldStop = true;
                            break;
                        }
                    } catch (parseError) {
                        console.error('Failed to parse build stream chunk', parseError, payload);
                    }
                }
            }
        } catch (error) {
            console.error('Full-Stack build failed', error);
            toast.error(error.message || 'Full-Stack build failed');
        } finally {
            setBuilding(false);
            document.body.classList.remove('agent-active');
        }
    };

    const createSnapshot = async (message) => {
        const pid = project?.project_id || projectId;
        if (!pid) return;

        try {
            const response = await api.post(`/projects/${pid}/snapshots`, {
                message: message || 'Manual snapshot'
            });

            if (response.data.success) {
                toast.success('Snapshot created');
                loadSnapshots();
            }
        } catch (error) {
            toast.error('Failed to create snapshot');
        }
    };

    const restoreSnapshot = async (snapshotId) => {
        const pid = project?.project_id || projectId;
        try {
            const response = await api.post(
                `/projects/${pid}/snapshots/${snapshotId}/restore`
            );

            if (response.data.success) {
                toast.success('Project restored');
                loadProject();
            }
        } catch (error) {
            toast.error('Failed to restore snapshot');
        }
    };

    const discussWithAI = async () => {
        if (!prompt.trim()) {
            toast.error('Please enter a message');
            return;
        }

        const userMessage = prompt.trim();
        setPrompt('');
        setDiscussionLoading(true);

        // Add user message to history
        setDiscussionHistory(prev => [...prev, { role: 'user', content: userMessage }]);

        try {
            const response = await api.post('/discuss', {
                message: userMessage,
                project_id: project?.project_id || projectId || null,
                history: discussionHistory.map(msg => ({
                    role: msg.role,
                    content: msg.content
                }))
            });

            if (response.data?.response) {
                setDiscussionHistory(prev => [...prev, {
                    role: 'assistant',
                    content: response.data.response
                }]);
            } else {
                // Handle empty response
                setDiscussionHistory(prev => [...prev, {
                    role: 'assistant',
                    content: 'I received your message but couldn\'t generate a response. Please try again.'
                }]);
            }
        } catch (error) {
            console.error('Discussion failed:', error);
            const errorMessage = error.response?.data?.detail || 'Failed to get AI response';
            toast.error(errorMessage);
            // Add error message to history
            setDiscussionHistory(prev => [...prev, {
                role: 'assistant',
                content: `Sorry, I encountered an error: ${errorMessage}. Please try again.`
            }]);
        } finally {
            setDiscussionLoading(false);
        }
    };

    const openDeploymentConsole = () => {
        if (!project?.project_id && !projectId) {
            toast.error('Generate or select a project before deploying');
            return;
        }
        setShowDeploymentConsole(true);
    };

    const handleDeploymentComplete = (deploymentResult) => {
        toast.success(deploymentResult?.url ? `Deployed to ${deploymentResult.url}` : 'Deployment completed');
        setProject((prev) => (prev ? { ...prev, deployment: deploymentResult } : prev));
    };

    return (
        <div className="builder-root text-white" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'transparent' }}>

            {/* ========== MAIN BUILDER — fills the full screen ========== */}
            <div style={{ flex: 1, overflow: 'hidden', position: 'relative', zIndex: 5, display: 'flex', flexDirection: 'column' }}>

                {/* Top bar: status + nav */}
                <div className="w-full flex flex-col items-center py-2 px-4" style={{ flexShrink: 0 }}>

                    {/* Status indicator */}
                    <div className="flex items-center gap-2 px-4 py-1 rounded-full bg-slate-900/60 border border-emerald-500/30 backdrop-blur-sm">
                        <span className="text-emerald-400 animate-pulse text-lg">●</span>
                        <span className="text-slate-400 text-sm font-mono tracking-wider">SYSTEMS ONLINE</span>
                    </div>

                    {/* Navigation buttons */}
                    <div className="flex gap-3 mt-2">
                        <Button
                            variant="outline"
                            onClick={() => navigate('/home')}
                            className="border-slate-600/50 bg-slate-800/40 backdrop-blur-sm text-slate-300 hover:bg-slate-700/60 hover:text-white hover:border-slate-500"
                        >
                            ← Home
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setShowSettings(!showSettings)}
                            className="border-slate-600/50 bg-slate-800/40 backdrop-blur-sm text-slate-300 hover:bg-slate-700/60 hover:text-white hover:border-slate-500"
                            title="Visual Settings"
                        >
                            <Settings className="h-4 w-4 mr-2" />
                            Settings
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => navigate('/projects')}
                            className="border-slate-600/50 bg-slate-800/40 backdrop-blur-sm text-slate-300 hover:bg-slate-700/60 hover:text-white hover:border-slate-500"
                        >
                            My Projects
                        </Button>
                        {JSON.parse(localStorage.getItem('user') || '{}').is_admin && (
                            <Button
                                variant="outline"
                                onClick={() => navigate('/admin')}
                                className="border-purple-600/50 bg-purple-900/30 backdrop-blur-sm text-purple-300 hover:bg-purple-700/40 hover:text-white hover:border-purple-400"
                            >
                                <Shield className="h-4 w-4 mr-2" />
                                Admin
                            </Button>
                        )}
                        {projectId && (
                            <>
                                <Button
                                    variant="outline"
                                    onClick={() => createSnapshot()}
                                    className="border-slate-600/50 bg-slate-800/40 backdrop-blur-sm text-slate-300 hover:bg-slate-700/60 hover:text-white"
                                >
                                    <Save className="h-4 w-4 mr-2" />
                                    Snapshot
                                </Button>
                                <Button
                                    onClick={openDeploymentConsole}
                                    className="bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-400 hover:to-green-400 text-white shadow-lg shadow-emerald-500/25"
                                >
                                    <Upload className="h-4 w-4 mr-2" />
                                    Deploy
                                </Button>
                            </>
                        )}
                    </div>

                    {/* Settings dropdown */}
                    {showSettings && (
                        <div className="absolute top-full mt-2 glass-panel-strong rounded-lg p-4 min-w-[220px] z-50 shadow-2xl animate-fade-in">
                            <div className="text-sm font-medium text-white mb-3">Visual Settings</div>
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={backgroundMotion}
                                    onChange={(e) => setBackgroundMotion(e.target.checked)}
                                    className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500"
                                />
                                <span className="text-sm text-slate-300">Background Animation</span>
                            </label>
                            <p className="text-xs text-slate-500 mt-2">Disable for reduced motion</p>
                        </div>
                    )}
                </div>

                {/* ========== BUILDER PANELS ========== */}
                <div style={{ flex: 1, maxWidth: '1800px', marginLeft: 'auto', marginRight: 'auto', paddingLeft: '1.5rem', paddingRight: '1.5rem', paddingBottom: '0.5rem', width: '100%' }}>

                    {/* Mode Selector - Command Tabs */}
                    <Card className="glass-panel rounded-xl p-2">
                        <div className="grid grid-cols-5 gap-2">
                            <Button
                                variant={mode === 'build' ? 'default' : 'outline'}
                                onClick={() => setMode('build')}
                                className={mode === 'build'
                                    ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white shadow-lg shadow-orange-500/25 border-0'
                                    : 'border-slate-600/40 bg-slate-800/30 text-slate-400 hover:text-white hover:bg-slate-700/50 hover:border-orange-500/30'}
                            >
                                <Rocket className="h-4 w-4 mr-2" />
                                Full-Stack Build
                            </Button>
                            <Button
                                variant={mode === 'chat' ? 'default' : 'outline'}
                                onClick={() => setMode('chat')}
                                className={mode === 'chat'
                                    ? 'bg-gradient-to-r from-cyan-500 to-teal-500 text-white shadow-lg shadow-cyan-500/25 border-0'
                                    : 'border-slate-600/40 bg-slate-800/30 text-slate-400 hover:text-white hover:bg-slate-700/50 hover:border-cyan-500/30'}
                            >
                                <Bot className="h-4 w-4 mr-2" />
                                AI Chat
                            </Button>
                            <Button
                                variant={mode === 'discuss' ? 'default' : 'outline'}
                                onClick={() => setMode('discuss')}
                                className={mode === 'discuss'
                                    ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/25 border-0'
                                    : 'border-slate-600/40 bg-slate-800/30 text-slate-400 hover:text-white hover:bg-slate-700/50 hover:border-blue-500/30'}
                            >
                                <MessageCircle className="h-4 w-4 mr-2" />
                                Discussion Mode
                            </Button>
                            <Button
                                variant={mode === 'edit' ? 'default' : 'outline'}
                                onClick={() => setMode('edit')}
                                className={mode === 'edit'
                                    ? 'bg-gradient-to-r from-purple-500 to-violet-500 text-white shadow-lg shadow-purple-500/25 border-0'
                                    : 'border-slate-600/40 bg-slate-800/30 text-slate-400 hover:text-white hover:bg-slate-700/50 hover:border-purple-500/30'}
                                disabled={!project}
                            >
                                <Paintbrush className="h-4 w-4 mr-2" />
                                Visual Editor
                            </Button>
                            <Button
                                variant={mode === 'history' ? 'default' : 'outline'}
                                onClick={() => setMode('history')}
                                className={mode === 'history'
                                    ? 'bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg shadow-emerald-500/25 border-0'
                                    : 'border-slate-600/40 bg-slate-800/30 text-slate-400 hover:text-white hover:bg-slate-700/50 hover:border-emerald-500/30'}
                                disabled={!projectId && !project?.project_id}
                            >
                                <History className="h-4 w-4 mr-2" />
                                Version History
                            </Button>
                        </div>
                    </Card>

                    {/* Main Content Area */}
                    <ResizablePanelGroup
                        direction={isMobileLayout ? 'vertical' : 'horizontal'}
                        className="h-[calc(100vh-300px)] gap-4"
                    >

                        {/* Left Panel - Input/Control */}
                        <ResizablePanel
                            minSize={isMobileLayout ? 40 : 35}
                            defaultSize={isMobileLayout ? 48 : 38}
                            className="min-h-[320px]"
                        >
                            <Card className="h-full glass-panel rounded-xl p-6 flex flex-col overflow-hidden">
                                {mode === 'build' && (
                                    <div className="flex flex-col h-full overflow-y-auto">
                                        {/* Agent Header - Command Interface Style */}
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="p-2.5 rounded-xl bg-gradient-to-br from-orange-500/20 to-amber-500/20 border border-orange-500/30 glow-orange">
                                                <Zap className="h-5 w-5 text-orange-400" />
                                            </div>
                                            <div className="flex-1">
                                                <h2 className="text-xl font-bold text-high-contrast flex items-center gap-2">
                                                    Full-Stack Builder
                                                    <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/20 border border-orange-500/30 text-orange-300 font-medium">
                                                        LIVE
                                                    </span>
                                                </h2>
                                                <p className="text-slate-400 text-xs">
                                                    Self-testing • Auto-fixing • Up to 200 min runtime
                                                </p>
                                            </div>
                                        </div>

                                        {/* ── Sub-tabs: Input / Console / Report ── */}
                                        <div className="flex gap-1 mb-3 bg-slate-800/50 rounded-lg p-1">
                                            <button
                                                onClick={() => setConsoleTab('input')}
                                                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${consoleTab === 'input'
                                                    ? 'bg-orange-500/20 text-orange-300 border border-orange-500/30'
                                                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                                                    }`}
                                            >
                                                <Rocket className="h-3.5 w-3.5" />
                                                Input
                                            </button>
                                            <button
                                                onClick={() => setConsoleTab('console')}
                                                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${consoleTab === 'console'
                                                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/30'
                                                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                                                    }`}
                                            >
                                                <Terminal className="h-3.5 w-3.5" />
                                                Console
                                                {building && (
                                                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
                                                )}
                                            </button>
                                            <button
                                                onClick={() => setConsoleTab('report')}
                                                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all ${consoleTab === 'report'
                                                    ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                                                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                                                    }`}
                                            >
                                                <FileBarChart className="h-3.5 w-3.5" />
                                                Report
                                                {buildSummary && !building && (
                                                    <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                                                )}
                                            </button>
                                        </div>

                                        {/* ── Input Tab ── */}
                                        {consoleTab === 'input' && (
                                            <div className="flex flex-col flex-1 overflow-y-auto">
                                                {/* Command Input Area */}
                                                <div className="command-input rounded-xl p-4 mb-3">
                                                    {/* Voice Input Header */}
                                                    <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-700/30">
                                                        <span className="text-xs text-slate-400 uppercase tracking-wide font-medium">
                                                            Build Prompt
                                                        </span>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs text-slate-500">Voice</span>
                                                            <VoiceInputButton
                                                                onTranscript={(text) => setPrompt(prev => prev + ' ' + text)}
                                                                disabled={building}
                                                                size="sm"
                                                            />
                                                        </div>
                                                    </div>
                                                    <textarea
                                                        value={prompt}
                                                        onChange={(e) => setPrompt(e.target.value)}
                                                        placeholder="Describe your app in detail... Your site will be researched, planned, built, tested, and verified.

Example: Build a real-time dashboard for tracking cryptocurrency prices with charts, alerts, and a dark mode theme."
                                                        className="w-full h-32 bg-transparent text-white placeholder-slate-500 resize-none focus:outline-none text-sm leading-relaxed"
                                                        disabled={building}
                                                    />

                                                    {/* Attached Files Display */}
                                                    {uploadedFiles.length > 0 && (() => {
                                                        const u = JSON.parse(localStorage.getItem('user') || '{}');
                                                        return u.is_admin === true;
                                                    })() && (
                                                            <div className="mt-3 pt-3 border-t border-slate-700/50">
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <span className="text-xs text-slate-400 uppercase tracking-wide font-medium">
                                                                        Context Files ({uploadedFiles.length})
                                                                    </span>
                                                                    <button
                                                                        onClick={() => setUploadedFiles([])}
                                                                        className="text-xs text-slate-500 hover:text-red-400 transition-colors"
                                                                    >
                                                                        Clear all
                                                                    </button>
                                                                </div>
                                                                <div className="flex flex-wrap gap-2">
                                                                    {uploadedFiles.map((file, idx) => (
                                                                        <div
                                                                            key={idx}
                                                                            className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700/50 group"
                                                                        >
                                                                            <FileText className="h-3.5 w-3.5 text-orange-400" />
                                                                            <span className="text-xs text-slate-300 max-w-[120px] truncate">
                                                                                {file.filename}
                                                                            </span>
                                                                            <button
                                                                                onClick={() => setUploadedFiles(prev => prev.filter((_, i) => i !== idx))}
                                                                                className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-slate-700 rounded transition-all"
                                                                            >
                                                                                <X className="h-3 w-3 text-slate-400 hover:text-red-400" />
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                </div>

                                                {/* Upload Zone - Collapsible (Admin only) */}
                                                <div className="mb-3">
                                                    {(() => {
                                                        return !showUploadZone ? (
                                                            <button
                                                                onClick={() => setShowUploadZone(true)}
                                                                disabled={building}
                                                                className="flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-orange-400 
                                                               bg-slate-800/40 hover:bg-slate-800/60 rounded-lg border border-slate-700/40 
                                                               hover:border-orange-500/30 transition-all w-full justify-center
                                                               disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                <Paperclip className="h-4 w-4" />
                                                                <span>Attach files, images, or project ZIP</span>
                                                            </button>
                                                        ) : (
                                                            <div className="animate-fade-in">
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <span className="text-xs text-slate-400 uppercase tracking-wide font-medium">
                                                                        Upload Context
                                                                    </span>
                                                                    <button
                                                                        onClick={() => setShowUploadZone(false)}
                                                                        className="text-xs text-slate-500 hover:text-white transition-colors"
                                                                    >
                                                                        Collapse
                                                                    </button>
                                                                </div>
                                                                <FileUploadZone
                                                                    onFilesUploaded={(files) => {
                                                                        setUploadedFiles(prev => [...prev, ...files]);
                                                                        toast.success(`${files.length} file(s) attached as context`);
                                                                    }}
                                                                    projectId={projectId}
                                                                    disabled={building}
                                                                    compact={false}
                                                                />
                                                            </div>
                                                        );
                                                    })()}
                                                </div>

                                                {/* Hero Image Upload — optional, data-URI embedded */}
                                                {(() => {
                                                    return (
                                                        <div className="flex items-center gap-3 mt-1">
                                                            <label
                                                                className="flex items-center gap-2 cursor-pointer text-xs text-slate-400 hover:text-orange-400 transition-colors"
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                </svg>
                                                                <span>{heroImage ? 'Hero image attached ✅' : 'Add hero image (optional, up to 5MB)'}</span>
                                                                <input
                                                                    type="file"
                                                                    accept="image/png,image/jpeg,image/webp,image/gif"
                                                                    className="hidden"
                                                                    onChange={handleHeroImageUpload}
                                                                    disabled={building}
                                                                />
                                                            </label>
                                                            {heroImage && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setHeroImage(null)}
                                                                    className="text-xs text-red-400 hover:text-red-300 transition-colors"
                                                                >
                                                                    Remove
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })()}

                                                {/* Build Action Button */}
                                                <div className="space-y-2">
                                                    {/* AI Model Selector — shown for Full AI mode */}
                                                    {buildMode === 'full_ai' && (
                                                        <div className="flex items-center gap-2">
                                                            <label className="text-xs text-slate-400 font-medium whitespace-nowrap">AI Model:</label>
                                                            <select
                                                                value={aiModel}
                                                                onChange={(e) => setAiModel(e.target.value)}
                                                                disabled={building}
                                                                className="flex-1 bg-slate-800/60 text-slate-200 text-sm rounded-lg px-3 py-1.5
                                                               border border-slate-700/50 focus:border-blue-500/50 focus:outline-none
                                                               transition-all disabled:opacity-50"
                                                            >
                                                                <option value="gpt-4o">GPT-4o</option>
                                                                <option value="gpt-4o-mini">GPT-4o Mini</option>
                                                                <option value="gpt-4-turbo">GPT-4 Turbo</option>
                                                                <option value="gpt-4.1">GPT-4.1</option>
                                                                <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
                                                                <option value="gpt-4.1-nano">GPT-4.1 Nano</option>
                                                                <option value="gpt-4.5-preview">GPT-4.5 Preview</option>
                                                                <option value="gpt-5">GPT-5</option>
                                                                <option value="gpt-5.2">GPT-5.2</option>
                                                                <option value="o1">o1 (Reasoning)</option>
                                                                <option value="o3">o3 (Reasoning)</option>
                                                                <option value="o3-mini">o3-mini (Reasoning)</option>
                                                                <option value="o4-mini">o4-mini (Reasoning)</option>
                                                            </select>
                                                        </div>
                                                    )}
                                                    {/* Build Mode Toggle - Fortress vs Full AI */}
                                                    <div className="flex items-center gap-2 bg-slate-800/60 rounded-lg p-1">
                                                        <button
                                                            onClick={() => setBuildMode('static')}
                                                            disabled={building}
                                                            className={`flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-all
                                                    ${buildMode === 'static'
                                                                    ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                                                                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'}`}
                                                        >
                                                            <Shield className="h-4 w-4" />
                                                            Fortress (Offline)
                                                        </button>
                                                        <button
                                                            onClick={() => setBuildMode('full_ai')}
                                                            disabled={building}
                                                            className={`flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-all
                                                    ${buildMode === 'full_ai'
                                                                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                                                    : 'text-slate-400 hover:text-white hover:bg-slate-700/50'}`}
                                                        >
                                                            <Wifi className="h-4 w-4" />
                                                            Full AI (Online)
                                                        </button>
                                                    </div>
                                                    {buildMode === 'full_ai' && (
                                                        <>
                                                            <div className="text-xs text-blue-400/80 bg-blue-500/10 rounded-lg px-3 py-2 border border-blue-500/20">
                                                                <Wifi className="h-3 w-3 inline mr-1" />
                                                                Online mode: may use Google Fonts, external images, embeds. A manifest will declare all dependencies.
                                                            </div>
                                                            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-300 hover:text-white transition-colors">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={aiImages}
                                                                    onChange={(e) => setAiImages(e.target.checked)}
                                                                    disabled={building}
                                                                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-purple-500 focus:ring-purple-500/50"
                                                                />
                                                                <span>🎨 Generate AI Images (DALL-E) — unique custom images</span>
                                                            </label>
                                                        </>
                                                    )}
                                                    <Button
                                                        onClick={startAutonomousBuild}
                                                        disabled={building}
                                                        className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 
                                                           h-12 text-lg font-semibold shadow-lg shadow-orange-500/25 
                                                           transition-all duration-300 hover:shadow-orange-500/40 hover:scale-[1.02]
                                                           disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                                    >
                                                        {building ? (
                                                            <>
                                                                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                                                {buildMode === 'full_ai' ? 'Full AI Building...' : 'Fortress Building...'}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Rocket className="h-5 w-5 mr-2" />
                                                                {buildMode === 'full_ai' ? 'Build with Full AI' : 'Build Fortress Site'}
                                                            </>
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}

                                        {/* ── Console Tab ── */}
                                        {consoleTab === 'console' && (
                                            <div className="flex-1 min-h-0">
                                                <AgentConsole
                                                    events={progress}
                                                    planner={planner}
                                                    research={research}
                                                    building={building}
                                                    buildSummary={buildSummary}
                                                />
                                            </div>
                                        )}

                                        {/* ── Report Tab ── */}
                                        {consoleTab === 'report' && (
                                            <div className="flex-1 min-h-0">
                                                <BuildReport
                                                    buildSummary={buildSummary}
                                                    layoutPlan={layoutPlanInfo}
                                                    visualProfile={visualProfile}
                                                    planner={planner}
                                                    research={research}
                                                    buildMode={buildMode}
                                                    artifacts={artifacts}
                                                />
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* ═══════════════════════════════════════════════════════════════════
                                    AI Chat Mode - Full featured chat with attachments & voice
                                    ═══════════════════════════════════════════════════════════════════ */}
                                {mode === 'chat' && (
                                    <div className="flex flex-col h-full">
                                        <ChatPanel
                                            projectId={project?.project_id || projectId}
                                            className="flex-1 rounded-xl overflow-hidden border border-slate-700/50"
                                        />
                                    </div>
                                )}

                                {mode === 'discuss' && (
                                    <div className="flex flex-col h-full">
                                        {/* Premium Header */}
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/20 to-purple-500/20 border border-blue-500/30">
                                                <Bot className="h-5 w-5 text-blue-400" />
                                            </div>
                                            <div>
                                                <h2 className="text-xl font-semibold text-high-contrast">AI Console</h2>
                                                <p className="text-slate-400 text-xs">
                                                    Plan • Brainstorm • Architecture Discussion
                                                </p>
                                            </div>
                                        </div>

                                        {/* File Upload Zone — Admin only */}
                                        {(() => {
                                            const u = JSON.parse(localStorage.getItem('user') || '{}');
                                            return (u.is_admin === true)
                                                ? <FileUploadZone onFilesUploaded={(files) => { }} compact={true} />
                                                : null;
                                        })()}

                                        {/* Premium Discussion History */}
                                        <div className="flex-1 glass-card rounded-xl p-4 overflow-y-auto mb-4 border border-slate-700/50 bg-gradient-to-b from-slate-900/50 to-slate-800/30">
                                            {discussionHistory.length === 0 ? (
                                                <div className="flex flex-col items-center justify-center py-12 text-center">
                                                    <div className="p-4 rounded-full bg-slate-800/50 border border-slate-700/50 mb-4">
                                                        <Cpu className="h-8 w-8 text-slate-500" />
                                                    </div>
                                                    <p className="text-slate-400 font-medium">Ready for your input</p>
                                                    <p className="text-slate-500 text-sm mt-1">Start a discussion about your project</p>
                                                </div>
                                            ) : (
                                                <div className="space-y-6">
                                                    {discussionHistory.map((msg, idx) => (
                                                        <div
                                                            key={idx}
                                                            className={`flex gap-3 animate-slide-up ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                                            style={{ animationDelay: `${idx * 0.05}s` }}
                                                        >
                                                            {/* AI Avatar - Left side */}
                                                            {msg.role !== 'user' && (
                                                                <div className="flex-shrink-0 mt-1">
                                                                    <div className="p-2 rounded-lg bg-gradient-to-br from-blue-600/30 to-purple-600/30 border border-blue-500/40 shadow-lg shadow-blue-500/10">
                                                                        <Bot className="h-4 w-4 text-blue-400" />
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Message Bubble */}
                                                            <div className={`max-w-[75%] ${msg.role === 'user' ? 'order-first' : ''}`}>
                                                                <div className={`p-4 rounded-2xl shadow-lg transition-all duration-300 hover:scale-[1.01] ${msg.role === 'user'
                                                                    ? 'bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-orange-500/20 rounded-br-md'
                                                                    : 'bg-slate-800/80 backdrop-blur-md text-slate-100 border border-slate-600/40 shadow-slate-900/50 rounded-bl-md'
                                                                    }`}>
                                                                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                                                                </div>
                                                                <p className={`text-xs text-slate-500 mt-1.5 px-2 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                                                                    {msg.role === 'user' ? 'You' : 'Digital Ninja AI'}
                                                                </p>
                                                            </div>

                                                            {/* User Avatar - Right side */}
                                                            {msg.role === 'user' && (
                                                                <div className="flex-shrink-0 mt-1">
                                                                    <div className="p-2 rounded-lg bg-gradient-to-br from-orange-500/30 to-red-500/30 border border-orange-500/40 shadow-lg shadow-orange-500/10">
                                                                        <User className="h-4 w-4 text-orange-400" />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}

                                                    {/* Premium Thinking Indicator */}
                                                    {discussionLoading && (
                                                        <div className="flex gap-3 justify-start animate-slide-up">
                                                            <div className="flex-shrink-0 mt-1">
                                                                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-600/30 to-purple-600/30 border border-blue-500/40 shadow-lg shadow-blue-500/10 animate-pulse">
                                                                    <Bot className="h-4 w-4 text-blue-400" />
                                                                </div>
                                                            </div>
                                                            <div className="thinking-indicator bg-slate-800/80 backdrop-blur-md p-4 rounded-2xl rounded-bl-md border border-slate-600/40 shadow-lg">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="flex gap-1">
                                                                        <span className="typing-dot w-2 h-2 bg-blue-400 rounded-full"></span>
                                                                        <span className="typing-dot w-2 h-2 bg-blue-400 rounded-full"></span>
                                                                        <span className="typing-dot w-2 h-2 bg-blue-400 rounded-full"></span>
                                                                    </div>
                                                                    <span className="text-slate-400 text-sm font-medium">Processing...</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Premium Input Area */}
                                        <div className="relative">
                                            <div className="flex gap-2 p-2 rounded-xl bg-slate-800/60 backdrop-blur-md border border-slate-700/50 shadow-lg">
                                                <label
                                                    className="p-3 rounded-lg hover:bg-slate-700/50 transition-colors text-slate-400 hover:text-orange-400 cursor-pointer"
                                                    title="Attach files"
                                                >
                                                    <Paperclip className="h-5 w-5" />
                                                    <input
                                                        type="file"
                                                        className="hidden"
                                                        multiple
                                                        accept="image/*,.pdf,.txt,.json,.html,.css,.js,.py,.md"
                                                        onChange={async (e) => {
                                                            const files = Array.from(e.target.files || []);
                                                            if (!files.length) return;
                                                            for (const file of files) {
                                                                try {
                                                                    const formData = new FormData();
                                                                    formData.append('file', file);
                                                                    const token = localStorage.getItem('token') || '';
                                                                    const resp = await fetch(`${BACKEND_URL}/api/upload`, {
                                                                        method: 'POST',
                                                                        headers: token ? { Authorization: `Bearer ${token}` } : {},
                                                                        body: formData,
                                                                    });
                                                                    if (resp.ok) {
                                                                        const data = await resp.json();
                                                                        setUploadedFiles(prev => [...prev, data]);
                                                                        toast.success(`Attached: ${file.name}`);
                                                                    } else {
                                                                        toast.error(`Failed to upload ${file.name}`);
                                                                    }
                                                                } catch (err) {
                                                                    toast.error(`Upload error: ${err.message}`);
                                                                }
                                                            }
                                                            e.target.value = '';
                                                        }}
                                                    />
                                                </label>
                                                <VoiceInputButton
                                                    onTranscript={(text) => setPrompt(prev => prev + text)}
                                                    disabled={discussionLoading}
                                                    className="p-3"
                                                />
                                                <input
                                                    type="text"
                                                    value={prompt}
                                                    onChange={(e) => setPrompt(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && !discussionLoading && discussWithAI()}
                                                    placeholder="Ask about features, architecture, trade-offs..."
                                                    className="flex-1 bg-transparent text-white px-3 py-2 focus:outline-none placeholder-slate-500"
                                                    disabled={discussionLoading}
                                                />
                                                <Button
                                                    onClick={discussWithAI}
                                                    className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 text-white shadow-lg shadow-blue-500/20 transition-all duration-300 hover:shadow-blue-500/40"
                                                    disabled={discussionLoading || !prompt.trim()}
                                                >
                                                    {discussionLoading ? (
                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                    ) : (
                                                        <Send className="h-4 w-4" />
                                                    )}
                                                </Button>
                                            </div>
                                            <p className="text-xs text-slate-500 mt-2 text-center">
                                                Free credit-efficient planning • No changes made to project
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {mode === 'history' && (
                                    <div className="flex flex-col h-full">
                                        <h2 className="text-xl font-semibold mb-4 text-high-contrast">Version History</h2>
                                        <p className="text-slate-300 text-sm mb-4">
                                            Snapshots are created automatically. Restore to any previous version.
                                        </p>

                                        <div className="flex-1 overflow-y-auto space-y-2">
                                            {snapshots.length === 0 ? (
                                                <div className="text-slate-400 text-center py-8">
                                                    No snapshots yet
                                                </div>
                                            ) : (
                                                snapshots.map((snapshot) => {
                                                    const summaryItems = normalizeResearchSummary(snapshot.research_summary);
                                                    return (
                                                        <Card key={snapshot.snapshot_id} className="glass-card p-4 hover:border-slate-500/60 transition-colors">
                                                            <div className="flex items-start justify-between">
                                                                <div className="flex-1">
                                                                    <div className="font-medium text-slate-100">{snapshot.message}</div>
                                                                    <div className="text-sm text-slate-400 mt-1">
                                                                        {new Date(snapshot.created_at).toLocaleString('en-ZA', { hour12: false, timeZone: 'Africa/Johannesburg' })}
                                                                    </div>
                                                                    <div className="text-xs text-slate-400 mt-1">
                                                                        {snapshot.file_count} files • {snapshot.auto_created ? 'Auto' : 'Manual'}
                                                                    </div>
                                                                    {snapshot.has_research && (
                                                                        <div className="mt-2">
                                                                            <div className="text-xs font-medium text-emerald-300 flex items-center gap-1">
                                                                                <Sparkles className="h-3 w-3" />
                                                                                Saved research insights
                                                                            </div>
                                                                            {summaryItems.length > 0 && (
                                                                                <div className="mt-1 space-y-1">
                                                                                    {summaryItems.slice(0, 3).map((item, idx) => (
                                                                                        <div key={idx} className="text-xs text-slate-200">
                                                                                            • {item}
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => restoreSnapshot(snapshot.snapshot_id)}
                                                                    variant="outline"
                                                                    className="border-slate-500/50 bg-slate-800/30 hover:bg-slate-700/50"
                                                                >
                                                                    Restore
                                                                </Button>
                                                            </div>
                                                        </Card>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                )}
                            </Card>
                        </ResizablePanel>

                        <ResizableHandle withHandle className="bg-slate-600/40 hover:bg-orange-500/50 transition-colors" />

                        {/* Right Panel - Preview/Editor */}
                        <ResizablePanel
                            minSize={isMobileLayout ? 50 : 45}
                            defaultSize={isMobileLayout ? 52 : 62}
                            className="min-h-[320px]"
                        >
                            <Card className="h-full glass-panel-strong rounded-xl overflow-hidden flex flex-col holo-border">
                                {!project ? (
                                    <div className="h-full flex items-center justify-center">
                                        <div className="text-center">
                                            <div className="relative mx-auto mb-6">
                                                <div className="absolute inset-0 bg-orange-500/20 blur-2xl rounded-full" />
                                                <div className="relative p-6 rounded-2xl bg-slate-800/40 border border-slate-700/50">
                                                    <Sparkles className="h-12 w-12 text-orange-400/70 animate-subtle-pulse" />
                                                </div>
                                            </div>
                                            <p className="text-medium-contrast text-lg font-medium">Ready to Build</p>
                                            <p className="text-slate-500 text-sm mt-1">Describe your app to start</p>
                                        </div>
                                    </div>
                                ) : mode === 'edit' ? (
                                    <VisualEditor
                                        files={project.files || []}
                                        onFilesUpdate={(updatedFiles) => {
                                            setProject((prev) => {
                                                if (!prev) {
                                                    return prev;
                                                }
                                                return prepareProjectForPreview({ ...prev, files: updatedFiles });
                                            });
                                        }}
                                    />
                                ) : (
                                    <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
                                        <TabsList className="bg-slate-800/70 backdrop-blur-sm border-b border-slate-600/50 w-full justify-start rounded-none">
                                            <TabsTrigger value="preview" className="flex items-center gap-2 data-[state=active]:bg-slate-700/70">
                                                <Eye className="h-4 w-4" />
                                                Preview
                                            </TabsTrigger>
                                            <TabsTrigger value="sections" className="flex items-center gap-2 data-[state=active]:bg-slate-700/70">
                                                <Layers className="h-4 w-4" />
                                                Sections
                                            </TabsTrigger>
                                            <TabsTrigger value="layout" className="flex items-center gap-2 data-[state=active]:bg-slate-700/70">
                                                <LayoutGrid className="h-4 w-4" />
                                                Layout
                                            </TabsTrigger>
                                            <TabsTrigger value="code" className="flex items-center gap-2 data-[state=active]:bg-slate-700/70">
                                                <Code2 className="h-4 w-4" />
                                                Code
                                            </TabsTrigger>
                                        </TabsList>

                                        <TabsContent value="preview" className="flex-1 min-h-0 m-0 data-[state=active]:flex data-[state=active]:flex-col">
                                            {/* Phase 2: Use StaticPreview for static/full_ai builds (HTML/CSS/JS), LivePreview for React apps */}
                                            {buildMode === 'static' || buildMode === 'full_ai' || artifacts ? (
                                                <StaticPreview
                                                    key={previewKey}
                                                    files={project?.files || []}
                                                    artifacts={artifacts || project?.artifacts}
                                                    projectId={project?.project_id || projectId}
                                                />
                                            ) : (
                                                <LivePreview files={project?.files || []} />
                                            )}
                                        </TabsContent>

                                        <TabsContent value="sections" className="flex-1 m-0 data-[state=active]:flex">
                                            <SectionEditor
                                                projectId={project?.project_id || projectId}
                                                files={project?.files || []}
                                                onUpdate={(filePath, sectionName) => {
                                                    // Refresh project files after section edit
                                                    if (project?.project_id || projectId) {
                                                        const pid = project?.project_id || projectId;
                                                        api.get(`/projects/${pid}`).then((res) => {
                                                            if (res.data) {
                                                                applyProjectResponse(res.data);
                                                            }
                                                        }).catch(() => { });
                                                    }
                                                }}
                                            />
                                        </TabsContent>

                                        {/* ── Task 8: Layout Pack Selector Tab ── */}
                                        <TabsContent value="layout" className="flex-1 m-0 p-4 overflow-y-auto">
                                            <div className="space-y-6 max-w-xl">
                                                {/* Layout Pack Dropdown */}
                                                <div className="space-y-2">
                                                    <label className="text-sm font-medium text-slate-300">Layout Pack</label>
                                                    <select
                                                        value={layoutPack}
                                                        onChange={(e) => setLayoutPack(e.target.value)}
                                                        disabled={building}
                                                        className="w-full bg-slate-800/60 text-slate-200 text-sm rounded-lg px-3 py-2
                                                               border border-slate-700/50 focus:border-orange-500/50 focus:outline-none
                                                               transition-all disabled:opacity-50"
                                                    >
                                                        <option value="auto">Auto (inferred from prompt)</option>
                                                        <optgroup label="Industry Packs">
                                                            <option value="finance">Finance &amp; Wealth</option>
                                                            <option value="legal">Legal &amp; Compliance</option>
                                                            <option value="retail">Retail &amp; Hospitality</option>
                                                            <option value="saas">SaaS / Technology</option>
                                                            <option value="health">Health &amp; Wellness</option>
                                                            <option value="education">Education &amp; Training</option>
                                                            <option value="hospitality">Hospitality &amp; Events</option>
                                                        </optgroup>
                                                        <optgroup label="Premium Packs">
                                                            <option value="finance_elite">Finance Elite</option>
                                                            <option value="legal_authority">Legal Authority</option>
                                                            <option value="saas_modern">SaaS Modern</option>
                                                            <option value="retail_showcase">Retail Showcase</option>
                                                            <option value="hospitality_luxury">Hospitality Luxury</option>
                                                            <option value="healthcare_trust">Healthcare Trust</option>
                                                            <option value="education_credibility">Education Credibility</option>
                                                            <option value="corporate_minimal">Corporate Minimal</option>
                                                        </optgroup>
                                                    </select>
                                                    <p className="text-xs text-slate-500">
                                                        Industry packs force a vertical. Premium packs set curated hero/nav/grid/CTA/footer defaults.
                                                    </p>
                                                </div>

                                                {/* Advanced Overrides (collapsible) */}
                                                <div className="border border-slate-700/50 rounded-lg overflow-hidden">
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowAdvancedLayout(!showAdvancedLayout)}
                                                        className="w-full flex items-center justify-between px-4 py-2.5 bg-slate-800/40
                                                               text-sm font-medium text-slate-300 hover:bg-slate-800/60 transition-colors"
                                                    >
                                                        <span className="flex items-center gap-2">
                                                            <Settings className="h-4 w-4 text-slate-400" />
                                                            Advanced Overrides
                                                        </span>
                                                        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${showAdvancedLayout ? 'rotate-180' : ''}`} />
                                                    </button>
                                                    {showAdvancedLayout && (
                                                        <div className="px-4 py-3 space-y-3 border-t border-slate-700/50">
                                                            {[
                                                                { key: 'hero_variant', label: 'Hero Style', options: ['split_grid', 'centered', 'asymmetric'] },
                                                                { key: 'nav_variant', label: 'Navigation', options: ['classic', 'minimal'] },
                                                                { key: 'grid_style', label: 'Grid Style', options: ['card_grid', 'alternating', 'magazine'] },
                                                                { key: 'cta_variant', label: 'CTA Style', options: ['standard', 'split', 'minimal'] },
                                                                { key: 'footer_variant', label: 'Footer Style', options: ['grid4', 'centered'] },
                                                            ].map(({ key, label, options }) => (
                                                                <div key={key} className="flex items-center gap-3">
                                                                    <label className="text-xs text-slate-400 w-24 shrink-0">{label}</label>
                                                                    <select
                                                                        value={layoutOverrides[key] || ''}
                                                                        onChange={(e) => {
                                                                            const val = e.target.value;
                                                                            setLayoutOverrides(prev => {
                                                                                const next = { ...prev };
                                                                                if (val) { next[key] = val; } else { delete next[key]; }
                                                                                return next;
                                                                            });
                                                                        }}
                                                                        disabled={building}
                                                                        className="flex-1 bg-slate-900/60 text-slate-200 text-xs rounded px-2 py-1.5
                                                                               border border-slate-700/40 focus:border-orange-500/40 focus:outline-none
                                                                               transition-all disabled:opacity-50"
                                                                    >
                                                                        <option value="">Auto</option>
                                                                        {options.map(opt => (
                                                                            <option key={opt} value={opt}>{opt.replace(/_/g, ' ')}</option>
                                                                        ))}
                                                                    </select>
                                                                </div>
                                                            ))}
                                                            {Object.keys(layoutOverrides).length > 0 && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setLayoutOverrides({})}
                                                                    className="text-xs text-orange-400 hover:text-orange-300 transition-colors"
                                                                >
                                                                    Reset all overrides
                                                                </button>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Resolved Layout Plan display */}
                                                {layoutPlanInfo && (
                                                    <div className="space-y-2">
                                                        <h4 className="text-sm font-medium text-slate-300">Resolved Layout Plan</h4>
                                                        <div className="bg-slate-900/60 rounded-lg p-3 space-y-1.5 border border-slate-700/40">
                                                            {[
                                                                ['Industry', layoutPlanInfo.industry],
                                                                ['Hero', layoutPlanInfo.hero_variant],
                                                                ['Navigation', layoutPlanInfo.nav_variant],
                                                                ['Grid', layoutPlanInfo.grid_style],
                                                                ['CTA', layoutPlanInfo.cta_variant],
                                                                ['Footer', layoutPlanInfo.footer_variant],
                                                                ['Sections', (layoutPlanInfo.sections || []).join(', ')],
                                                                ['Seed', layoutPlanInfo.seed_hex],
                                                            ].map(([label, value]) => (
                                                                <div key={label} className="flex items-center gap-2 text-xs">
                                                                    <span className="text-slate-500 w-20 shrink-0">{label}</span>
                                                                    <span className="text-slate-300 font-mono">{value || '—'}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                        <p className="text-xs text-slate-500">
                                                            Same prompt + same overrides = identical output (deterministic).
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        </TabsContent>

                                        <TabsContent value="code" className="flex-1 m-0 p-4 overflow-y-auto">
                                            <div className="space-y-4">
                                                {(project.files || []).map((file, idx) => (
                                                    <div key={idx} className="bg-slate-900 rounded-lg p-4">
                                                        <div className="text-sm font-mono text-orange-400 mb-2">{file.path}</div>
                                                        <pre className="text-xs text-slate-300 overflow-x-auto">
                                                            <code>{file.content}</code>
                                                        </pre>
                                                    </div>
                                                ))}
                                            </div>
                                        </TabsContent>
                                    </Tabs>
                                )}
                            </Card>
                        </ResizablePanel>
                    </ResizablePanelGroup>

                    {/* Ninja Guide Companion - Right Side Panel */}
                    <NinjaGuide
                        buildResult={buildSummary ? {
                            files: project?.files,
                            engine: layoutPlanInfo?.engine_id || visualProfile?.engine,
                            flowPolicy: layoutPlanInfo?.flow_policy || visualProfile?.flow_policy,
                            archetype: visualProfile?.archetype,
                            sections: layoutPlanInfo?.sections,
                            seed: layoutPlanInfo?.seed_hex,
                            buildTime: buildSummary?.buildTime,
                            originalPrompt: prompt,
                        } : null}
                        isBuilding={building}
                        error={progress.some(p => p?.type === 'error' || p?.message?.includes?.('ERROR')) ? progress.find(p => p?.type === 'error' || p?.message?.includes?.('ERROR'))?.message : null}
                        onExpansionClick={(expansionPrompt) => {
                            setPrompt(prev => prev + ' ' + expansionPrompt);
                            toast.success('Added to prompt: ' + expansionPrompt);
                        }}
                        onSuggestionClick={(suggestion) => {
                            setPrompt(prev => prev + ' ' + suggestion);
                            toast.success('Added suggestion to prompt');
                        }}
                        className="hidden lg:flex"
                    />
                </div>
            </div>

            <DeploymentConsole
                open={showDeploymentConsole}
                onOpenChange={setShowDeploymentConsole}
                projectId={project?.project_id || projectId}
                projectName={project?.name}
                onComplete={handleDeploymentComplete}
            />
        </div>
    );
};

export default EnhancedBuilder;
